import { Bullet, Circle, Label, p100, p50, percent, time } from "@amcharts/amcharts5";
import {
  AxisRendererX,
  AxisRendererY,
  CategoryAxis,
  GaplessDateAxis,
  LineSeries,
  SmoothedXLineSeries,
  StepLineSeries,
  ValueAxis,
  XYChart,
  XYCursor,
} from "@amcharts/amcharts5/xy";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { getChartInterval } from "@/helpers/charts";
import { parseDateByMetricWithMach } from "@/helpers/dataMapping";
import { axisTitle } from "@/settings/charts";
import { chartFontSettings, chartInitialSettings, getAxisDateFormat } from "@/settings/widgets";
import { ChartDataRecord } from "@/types/charts";

export default class LineChart extends BaseChart {
  interval!: time.ITimeInterval;

  initChart() {
    this.root.container.children.clear();
    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    this.data = this.mapData(this.data);
    this.interval = getChartInterval(this.settings.xAxis) as time.ITimeInterval;

    this.createChart();
    this.createRenderers();
    this.createAxes();
    this.createCursor();

    const series = [this.settings.yAxisSecondary, this.settings.yAxisPrimary];
    series.forEach((name, index) => this.createLineSeries(name, index));
    this.showLegend(series, (this.chart as XYChart).series.values as LegendData);
  }

  protected mapData(data: ChartDataRecord[]) {
    if (!Array.isArray(data)) return [];

    const isQuarter = this.settings.xAxis === "quarter";
    const isMonth = this.settings.xAxis === "month";

    return data.map((el) => {
      const raw = el[this.settings.xAxis] as string | number;
      let timestamp: number | undefined;

      if (isQuarter) {
        timestamp = undefined;
      } else {
        let parsed: Date;
        if (typeof el.timestamp === "number") {
          parsed = new Date(el.timestamp);
        } else {
          parsed = parseDateByMetricWithMach(raw, isMonth ? "month" : "day");
        }

        timestamp = parsed.getTime();
        if (isNaN(timestamp)) {
          console.warn(`Invalid timestamp for raw: ${raw}`);
        }
      }

      return {
        ...(isQuarter ? { [this.settings.xAxis]: raw } : { timestamp }),
        [this.settings.yAxisPrimary]: el[this.settings.yAxisPrimary] ?? 0,
        [this.settings.yAxisSecondary]: el[this.settings.yAxisSecondary] ?? 0,
      };
    });
  }

  protected createChart() {
    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        panX: true,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: this.layout,
        focusable: true,
      })
    );
  }

  protected createRenderers() {
    const x = AxisRendererX.new(this.root, { minGridDistance: 80 });
    x.labels.template.setAll({
      centerX: p50,
      ...chartFontSettings.label,
    });

    const yPrimary = AxisRendererY.new(this.root, {});
    yPrimary.grid.template.setAll({
      strokeWidth: 1,
      strokeDasharray: [10, 5],
    });
    yPrimary.labels.template.setAll({ ...chartFontSettings.label });

    const ySecondary = AxisRendererY.new(this.root, { opposite: true });
    ySecondary.labels.template.setAll({ ...chartFontSettings.label });

    this.renderers = { x, yPrimary, ySecondary };
  }

  protected createAxes() {
    const isQuarter = this.settings.xAxis === "quarter";

    if (isQuarter) {
      const xAxis = (this.chart as XYChart).xAxes.push(
        CategoryAxis.new(this.root, {
          categoryField: this.settings.xAxis,
          renderer: this.renderers.x as AxisRendererX,
          startLocation: 0.5,
          endLocation: 0.5,
        })
      );

      xAxis.data.setAll(this.data);

      xAxis.get("renderer").labels.template.adapters.add("text", (text, target) => {
        const data = target.dataItem?.dataContext as Record<string, string | number | Date>;
        const raw = data?.[this.settings.xAxis];

        const parsedDate = parseDateByMetricWithMach(raw, "quarter");
        if (isNaN(parsedDate.getTime())) return text;

        const quarter = Math.floor(parsedDate.getUTCMonth() / 3) + 1;
        const year = String(parsedDate.getUTCFullYear()).slice(2);

        return `Q${quarter} ${year}`;
      });

      this.axes = { x: xAxis };
    } else {
      const isMonth = this.settings.xAxis === "month";

      const xAxis = (this.chart as XYChart).xAxes.push(
        GaplessDateAxis.new(this.root, {
          baseInterval: { timeUnit: this.settings.xAxis as "day" | "month", count: 1 },
          groupData: false,
          strictMinMax: true,
          markUnitChange: true,
          renderer: this.renderers.x as AxisRendererX,
          dateFormats: getAxisDateFormat(this.settings.xAxis),
          periodChangeDateFormats: getAxisDateFormat(this.settings.xAxis),
          start: 0,
          end: 1,
          maxDeviation: 0.01,
          endLocation: isMonth ? 0.0001 : undefined,
        })
      );

      xAxis.get("renderer").labels.template.set("location", isMonth ? 0 : 0.5);
      xAxis.get("renderer").grid.template.set("location", isMonth ? 0 : 0.5);

      this.axes = { x: xAxis };
    }

    const yPrimary = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        renderer: this.renderers.yPrimary as AxisRendererY,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.yAxisPrimary, this.data),
      })
    );

    const ySecondary = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        renderer: this.renderers.ySecondary as AxisRendererY,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.yAxisSecondary, this.data),
      })
    );

    if (this.settings.xAxisTitle) {
      this.axes.x.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisTitle,
          ...chartFontSettings.axisTitle,
          x: p50,
          centerX: p50,
          marginTop: axisTitle.marginTop,
        })
      );
    }

    if (this.settings.yAxisPrimaryTitle) {
      yPrimary.children.unshift(
        Label.new(this.root, {
          text: this.settings.yAxisPrimaryTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    if (this.settings.yAxisSecondaryTitle) {
      ySecondary.children.push(
        Label.new(this.root, {
          text: this.settings.yAxisSecondaryTitle,
          ...chartFontSettings.axisTitle,
          rotation: 90,
          x: p100,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    this.axes = { ...this.axes, yPrimary, ySecondary };
  }

  protected createCursor() {
    (this.chart as XYChart).set("cursor", XYCursor.new(this.root, { xAxis: this.axes.x }));
  }

  protected formatDate(timestamp: number) {
    const date = new Date(timestamp);
    return `${date.getUTCMonth() + 1}-${date.getUTCDate()}-${date.getUTCFullYear()}`;
  }

  protected createLineSeries(seriesName: string, index: number) {
    const isQuarter = this.settings.xAxis === "quarter";
    const isMonth = this.settings.xAxis === "month";
    const seriesType = this.options.seriesType === "StepLineSeries" ? StepLineSeries : SmoothedXLineSeries;

    const series = (this.chart as XYChart).series.push(
      seriesType.new(this.root, {
        name: seriesName,
        xAxis: this.axes.x,
        yAxis: index ? this.axes.yPrimary : this.axes.ySecondary,
        valueYField: seriesName,
        [isQuarter ? "categoryXField" : "valueXField"]: isQuarter ? this.settings.xAxis : "timestamp",
        fill: this.getSeriesColor(index),
        stroke: this.getSeriesColor(index),
        minBulletDistance: 10,
        locationX: isMonth ? 0 : 0.5,
      })
    ) as LineSeries;

    series.strokes.template.setAll({
      strokeWidth: this.settings.lineThickness,
    });

    if (this.options.showBullets) {
      series.bullets.push(() => {
        const bulletCircle = Circle.new(this.root, {
          radius: 4,
          fill: series.get("fill"),
          strokeWidth: 2,
          stroke: series.get("stroke"),
        });

        return Bullet.new(this.root, {
          sprite: bulletCircle,
          locationX: isMonth ? 0 : 0.5,
        });
      });
    }

    this.createTooltip(series, (tooltipLabel) => {
      const param: { key: string; value: unknown }[] = [];

      const tooltipData = (tooltipLabel.dataItem?.dataContext ?? {}) as Record<string, string | number | Date>;

      if (!tooltipData) return [];

      const isQuarter = this.settings.xAxis === "quarter";
      const dateKey = this.settings.xAxis;

      if (isQuarter) {
        const raw: string | number | Date = tooltipData[dateKey];
        const parsedDate = parseDateByMetricWithMach(raw, "quarter");

        if (!isNaN(parsedDate.getTime())) {
          const quarter = Math.floor(parsedDate.getUTCMonth() / 3) + 1;
          const year = String(parsedDate.getUTCFullYear()).slice(2);
          param.push({
            key: dateKey,
            value: `Q${quarter} ${year}`,
          });
        } else {
          param.push({
            key: dateKey,
            value: raw,
          });
        }
      } else {
        param.push({
          key: dateKey,
          value: this.formatDate(tooltipData.timestamp as number),
        });
      }

      param.push({
        key: seriesName,
        value: tooltipData[seriesName],
      });

      return param;
    });

    this.createLegendLabel(series, seriesName);
    this.updateLineSeriesData(seriesName, series);
  }

  protected updateLineSeriesData(seriesName: string, series: LineSeries) {
    const isQuarter = this.settings.xAxis === "quarter";
    const data = this.data.map((el) => ({
      [isQuarter ? this.settings.xAxis : "timestamp"]: isQuarter ? el[this.settings.xAxis] : el.timestamp,
      [seriesName]: el[seriesName],
    }));

    series.data.setAll(data);
  }
}
